import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tenantApi, propertyApi } from '../services/api';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/currency';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ReceiptRefundIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Tenants = () => {
  const { propertyId } = useParams();
  const [tenants, setTenants] = useState([]);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchTenants = async () => {
    setLoading(true);
    try {
      let response;
      if (propertyId) {
        // Fetch tenants for a specific property
        response = await tenantApi.getByProperty(propertyId);
        
        // Also fetch the property details
        const propertyResponse = await propertyApi.getById(propertyId);
        setProperty(propertyResponse.data);
      } else {
        // Fetch all tenants
        response = await tenantApi.getAll();
      }
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to load tenants');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTenants();
  }, [propertyId]);
  
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tenant? This will also delete all associated receipts.')) {
      try {
        await tenantApi.delete(id);
        toast.success('Tenant deleted successfully');
        fetchTenants();
      } catch (error) {
        toast.error('Failed to delete tenant');
        console.error(error);
      }
    }
  };
  
  // Format lease dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return format(new Date(dateString), 'MMM d, yyyy');
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          {propertyId && property ? (
            <div className="flex items-center">
              <Link
                to="/properties"
                className="text-gray-600 hover:text-gray-900 mr-2"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Tenants for {property.name}
                </h1>
                <p className="text-gray-600">{property.address}, {property.city}</p>
              </div>
            </div>
          ) : (
            <h1 className="text-2xl font-bold text-gray-900">All Tenants</h1>
          )}
        </div>
        <Link
          to={propertyId ? `/properties/${propertyId}/tenants/new` : '/tenants/new'}
          className="btn-primary inline-flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-1" />
          Add Tenant
        </Link>
      </div>
      
      {loading ? (
        <div className="text-center py-8">Loading tenants...</div>
      ) : tenants.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tenants found</h3>
          <p className="text-gray-600 mb-4">
            {propertyId
              ? `Start by adding your first tenant to ${property?.name || 'this property'}.`
              : 'Start by adding your first tenant to the system.'}
          </p>
          <Link
            to={propertyId ? `/properties/${propertyId}/tenants/new` : '/tenants/new'}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Add Tenant
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {tenants.map((tenant) => (
              <li key={tenant.id}>
                <div className="block hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="truncate text-lg font-medium text-primary-600">
                        {tenant.firstName} {tenant.lastName}
                      </p>
                      <div className="flex flex-shrink-0 space-x-2">
                        <Link
                          to={`/tenants/${tenant.id}/receipts/new`}
                          className="btn-primary inline-flex items-center text-sm"
                        >
                          <ReceiptRefundIcon className="h-4 w-4 mr-1" />
                          Generate Receipt
                        </Link>
                        <Link
                          to={`/tenants/${tenant.id}/email-template`}
                          className="text-indigo-600 hover:text-indigo-800 p-2"
                          title="Email Template"
                        >
                          <EnvelopeIcon className="h-5 w-5" />
                        </Link>
                        <Link
                          to={`/tenants/edit/${tenant.id}`}
                          className="text-blue-600 hover:text-blue-800 p-2"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(tenant.id)}
                          className="text-red-600 hover:text-red-800 p-2"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        {!propertyId && (
                          <p className="flex items-center text-sm text-gray-500 mr-6">
                            <BuildingOfficeIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                            Property #{tenant.propertyId}
                          </p>
                        )}
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          <span className="text-gray-900 font-medium">{formatCurrency(tenant.basicRent, tenant.currency || 'USD')}</span>
                          <span className="mx-1">+</span>
                          <span className="text-gray-900 font-medium">{formatCurrency(tenant.charges, tenant.currency || 'USD')}</span>
                          <span className="mx-1">=</span>
                          <span className="text-gray-900 font-medium">{formatCurrency(tenant.totalRent, tenant.currency || 'USD')}</span>
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <span className="mr-1.5">Lease:</span>
                        <span>{formatDate(tenant.leaseStart)}</span>
                        <span className="mx-1">to</span>
                        <span>{formatDate(tenant.leaseEnd)}</span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-4">
                      <Link
                        to={`/tenants/${tenant.id}/receipts`}
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                      >
                        View Receipts →
                      </Link>
                      <Link
                        to={`/tenants/${tenant.id}/yearly-summary`}
                        className="inline-flex items-center text-gray-500 hover:text-gray-700 text-sm font-medium"
                      >
                        <ChartBarIcon className="h-4 w-4 mr-1" />
                        Yearly Summary
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Tenants;